import captureError from '@disco/utils/captureError'
import uniqBy from 'lodash/uniqBy'
import NavigationService from '@disco/state-management/NavigationService'
import type { CopyMetadataFields } from 'types/copy-metadata'
import type { Thunk } from 'types/redux'
import type { NativeTag, Track, TrackId } from 'types/track'
import api from '@disco/api/server'
import { SET_COPY_METADATA, TRACKS_UPDATE } from '@disco/constants/ActionTypes'
import { AUDIO_FILE, VIDEO_FILE } from '@disco/constants/FileTypes'
import type { CopyMetadataState } from '@disco/state-management/reducers/copy-metadata'
import {
  selectCopyMetadataSourceTrack,
  selectCopyMetadataState,
} from '@disco/state-management/selectors/copy-metadata'
import { pick } from '@disco/utils/array'
import { getFileTypeByExt } from '@disco/utils/file-utils'
import { pushUINotification } from '@disco/utils/notifications'
import { plural } from '@disco/utils/string-utils'
const REQUEST_DELAY = 10

/**
 * Helper function to extract native tag IDs from a tags array.
 * @param tags - Array of native tags or null/undefined
 * @returns Array of native tag IDs
 */
function getNativeTagsIds(
  tags: NativeTag[] | null | undefined,
): NativeTag['id'][] {
  if (tags) {
    return tags.map((tag: NativeTag) => tag.id)
  }

  return []
}

/**
 * Sets the source and target tracks for metadata copying operation.
 * @param options - Configuration for copy metadata operation
 * @param options.sourceTrackId - ID of the track to copy metadata from
 * @param options.targetTrackIds - Array of track IDs to copy metadata to
 * @returns Action object to set copy metadata state
 */
export function setCopyMetadata({
  sourceTrackId,
  targetTrackIds,
  returnPath,
}: Partial<CopyMetadataState>): CopyMetadataState & {
  type: string
  sourceTrackId: TrackId | null | undefined
  targetTrackIds: TrackId[]
  returnPath?: string
} {
  return {
    type: SET_COPY_METADATA,
    sourceTrackId,
    targetTrackIds: targetTrackIds ?? [],
    returnPath,
  }
}
/**
 * Copies selected metadata fields from source track to target tracks.
 * @param fields - Configuration specifying which metadata fields to copy
 * @param fields.artwork - Whether to copy artwork
 * @param fields.comments_indexes - Indexes of comments to copy
 * @param fields.metadata_keys - Metadata keys to copy
 * @param fields.native_tags_indexes - Indexes of native tags to copy
 * @param fields.track_writers_indexes - Indexes of track writers to copy
 * @returns Thunk that performs metadata copying with undo functionality
 */
export function copyMetadata({
  artwork,
  comments_indexes,
  metadata_keys,
  native_tags_indexes,
  track_writers_indexes,
}: CopyMetadataFields): Thunk {
  return async (dispatch, getState) => {
    const state = getState()
    const { sourceTrackId, targetTrackIds, returnPath } =
      selectCopyMetadataState(state)
    const sourceTrack: Track | null | undefined =
      selectCopyMetadataSourceTrack(state)

    if (!sourceTrack) {
      return
    }

    const newMetadata = pick(
      sourceTrack.metadata,
      Object.keys(metadata_keys).filter(k => metadata_keys[k]),
    )

    const native_tags = Object.keys(native_tags_indexes)
      .map((key: string) => {
        const index = Number(key)

        if (native_tags_indexes[key] && sourceTrack.native_tags) {
          return sourceTrack.native_tags[index]
        }

        return null
      })
      .filter(tag => tag !== null && tag !== undefined)

    const track_comments = Object.keys(comments_indexes)
      .map((key: string) => {
        const index = Number(key)

        if (comments_indexes[key] && sourceTrack.comments?.[index]) {
          const { track, track_id, url, user, ...rest } =
            sourceTrack.comments[index]
          return { ...rest, user: user.id }
        }

        return false
      })
      .filter(Boolean)
    const previousState: Partial<Track>[] = []
    const futureState: Partial<Track>[] = []
    const track_writers = Object.keys(track_writers_indexes)
      .map((key: string) => {
        const index = Number(key)

        if (track_writers_indexes[key] && sourceTrack.track_writers?.[index]) {
          const {
            pro: proId,
            publisher: publisherId,
            split,
            writer: writerId,
          } = sourceTrack.track_writers[index]
          return {
            pro: proId
              ? {
                  id: proId,
                }
              : null,
            publisher: publisherId
              ? {
                  id: publisherId,
                }
              : null,
            split,
            writer: {
              id: writerId,
            },
            track_ids: targetTrackIds,
          }
        }

        return false
      })
      .filter(Boolean)

    const handleError = error => {
      dispatch({
        type: TRACKS_UPDATE,
        tracks: previousState,
      })
      pushUINotification({
        dismissible: 'none',
        level: 'error',
        message:
          'There was an error trying to copy track info. Please try again or contact us.',
        action: {
          label: 'Retry',
          callback: () => {
            dispatch(
              setCopyMetadata({
                sourceTrackId,
                targetTrackIds,
                returnPath,
              }),
            )
            NavigationService.push('/explorer/copy-metadata')
          },
        },
      })
      captureError(error, { extra: { level: 'info' } })
    }

    const update = {
      track_id_for_artwork:
        artwork && sourceTrack.artwork ? sourceTrackId : undefined,
      metadata: newMetadata,
      tags: getNativeTagsIds(native_tags),
      track_comments,
      track_writers,
    }

    for (const id of targetTrackIds) {
      const track: Track = state.tracks[id]
      const type = getFileTypeByExt(track.ext)
      let previousArtwork = {}
      let futureArtwork = {}

      if (
        artwork &&
        sourceTrack.artwork &&
        [AUDIO_FILE, VIDEO_FILE].includes(type)
      ) {
        // TODO: refactor copyMetadata/bulkUpdateTrackMetadata duplication
        previousArtwork = pick(track, ['artwork', 'artwork_sizes'])
        futureArtwork = pick(sourceTrack, ['artwork', 'artwork_sizes'])
      }

      // TODO: refactor copyMetadata/bulkUpdateTrackMetadata duplication
      const oldAttrs = pick(track, ['name', 'album', 'artist'])
      const newAttrs: Partial<Track> = {}
      if ('Title' in newMetadata) newAttrs.name = newMetadata.Title
      if ('Album' in newMetadata) newAttrs.album = newMetadata.Album
      if ('Artist' in newMetadata) newAttrs.artist = newMetadata.Artist
      previousState.push({
        ...previousArtwork,
        id: track.id,
        ...oldAttrs,
        metadata: track.metadata,
        native_tags: track.native_tags,
      })
      futureState.push({
        ...futureArtwork,
        id: track.id,
        metadata: { ...track.metadata, ...newMetadata },
        ...newAttrs,
        native_tags: uniqBy(
          [...(track.native_tags ?? []), ...native_tags],
          'id',
        ),
      })
    }

    const timeout: ReturnType<typeof setTimeout> = setTimeout(async () => {
      try {
        // @ts-expect-error - TODO: fix this
        await api.bulkUpdateTrackMetadata(sourceTrackId, targetTrackIds, update)
      } catch (error) {
        handleError(error)
      }
    }, REQUEST_DELAY * 1000)
    dispatch({
      type: TRACKS_UPDATE,
      tracks: futureState,
    })

    const undo = () => {
      clearTimeout(timeout)
      dispatch({
        type: TRACKS_UPDATE,
        tracks: previousState,
      })
    }

    pushUINotification({
      autoDismiss: REQUEST_DELAY,
      level: 'success',
      title: 'Track info copied!',
      message: `Track info was copied to ${plural(
        targetTrackIds.length,
        'track',
        'tracks',
      )}.`,
      action: {
        label: 'Undo',
        callback: undo,
      },
    })
  }
}
